import { useEffect, useRef, useState, type ReactNode } from 'react'

import { ChevronDown } from 'lucide-react'

/** One pickable value in a filter dropdown: the raw facet value, its display label, and how many
 *  rows carry it under the CURRENT other filters (own facet excluded, so options never vanish). */
export interface FacetOption {
  value: string
  label: string
  count: number
}

/** One checkbox/radio row: label left, count right; zero-count options dim but stay pickable. */
export function OptionRow({ checked, label, count, swatch, radio, onToggle }: {
  checked: boolean
  label: string
  count: number
  swatch?: string
  radio?: boolean
  onToggle: () => void
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-2 rounded-[7px] px-2 py-[5px] transition-colors hover:bg-[#f5f7fa] ${
        count === 0 && !checked ? 'opacity-45' : ''
      }`}
    >
      <input
        type={radio ? 'radio' : 'checkbox'}
        checked={checked}
        onChange={onToggle}
        className="h-[13px] w-[13px] accent-[#1e2a52]"
      />
      {swatch && <span className="h-[9px] w-[9px] flex-none rounded-[3px]" style={{ background: swatch }} />}
      <span className={`min-w-0 flex-1 truncate text-[12px] ${checked ? 'font-bold text-gis-ink' : 'font-medium text-gis-ink-2'}`}>
        {label}
      </span>
      <span className="text-[11px] tabular-nums text-gis-muted">{count}</span>
    </label>
  )
}

/**
 * One toolbar filter dropdown: a trigger (name + active-count badge, or a `label: value` text) over
 * an outside-click-closing popover of option rows. Every filter control in the GIS toolbars (Issues
 * AND Coverage) is one of these, so the rows read as a uniform strip of peers.
 */
export function FilterDropdown({ label, valueText, activeCount, children }: {
  label: string
  /** Single-select dropdowns show `label: value` on the trigger instead of a count badge. */
  valueText?: string
  activeCount?: number
  children: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const active = (activeCount ?? 0) > 0 || valueText !== undefined
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-[30px] items-center gap-[5px] rounded-[9px] border px-[9px] text-[12px] font-semibold whitespace-nowrap transition-colors ${
          active || open
            ? 'border-[#c7d2ea] bg-[#eef2fb] text-gis-navy'
            : 'border-gis-line bg-white text-gis-ink-2 hover:border-[#d6dbe6]'
        }`}
      >
        {valueText !== undefined ? `${label}: ${valueText}` : label}
        {(activeCount ?? 0) > 0 && (
          <span className="grid h-[16px] min-w-[16px] place-items-center rounded-full bg-gis-navy px-1 text-[10px] font-bold text-white tabular-nums">
            {activeCount}
          </span>
        )}
        <ChevronDown className="h-[12px] w-[12px] text-gis-muted" aria-hidden />
      </button>
      {open && (
        <div className="absolute top-[34px] left-0 z-40 max-h-[min(48vh,380px)] w-[236px] overflow-y-auto rounded-[12px] border border-slate-900/10 bg-white p-1.5 shadow-xl">
          {children}
        </div>
      )}
    </div>
  )
}

export function MultiFilterDropdown({ label, options, active, onToggle }: {
  label: string
  options: FacetOption[]
  active: readonly string[]
  onToggle: (value: string) => void
}) {
  return (
    <FilterDropdown label={label} activeCount={active.length}>
      {options.length === 0 ? (
        <div className="px-2 py-3 text-center text-[11.5px] text-gis-muted">No values in this view.</div>
      ) : (
        options.map((o) => (
          <OptionRow
            key={o.value}
            checked={active.includes(o.value)}
            label={o.label}
            count={o.count}
            onToggle={() => onToggle(o.value)}
          />
        ))
      )}
    </FilterDropdown>
  )
}

/** Distinct values from the full row set, counted over the except-own-facet filtered set — options
 *  never vanish when picked, and counts always reflect the other active filters. */
export function buildFacetOptions<T>(
  all: T[],
  countable: T[],
  key: (row: T) => string | null,
  label: (value: string) => string = (v) => v,
): FacetOption[] {
  const counts = new Map<string, number>()
  for (const row of countable) {
    const v = key(row)
    if (v !== null) {
      counts.set(v, (counts.get(v) ?? 0) + 1)
    }
  }
  const values = new Set<string>()
  for (const row of all) {
    const v = key(row)
    if (v !== null) {
      values.add(v)
    }
  }
  return [...values]
    .map((value) => ({ value, label: label(value), count: counts.get(value) ?? 0 }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}
